import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { login as apiLogin, logout as apiLogout, getMe, requestMagicLink, verifyMagicLink, setupPassword } from '@/api/auth'
import { useAuthStore } from '@/store/auth'

export function useMe() {
  const setUser = useAuthStore((s) => s.setUser)
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const me = await getMe()
      setUser(me)
      return me
    },
    retry: false,
  })
}

export function useLogin() {
  const setUser = useAuthStore((s) => s.setUser)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      apiLogin(email, password),
    onSuccess: async () => {
      const me = await getMe()
      setUser(me)
      qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export function useLogout() {
  const setUser = useAuthStore((s) => s.setUser)
  const qc = useQueryClient()
  const navigate = useNavigate()
  return useMutation({
    mutationFn: apiLogout,
    onSuccess: () => {
      setUser(null)
      qc.clear()
      navigate('/login', { replace: true })
    },
  })
}

export function useRequestMagicLink() {
  return useMutation({
    mutationFn: (email: string) => requestMagicLink(email),
  })
}

export function useVerifyMagicLink() {
  const setUser = useAuthStore((s) => s.setUser)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) => verifyMagicLink(token),
    onSuccess: async () => {
      const me = await getMe()
      setUser(me)
      qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export function useSetupPassword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (password: string) => setupPassword(password),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}
